import axios, { type AxiosRequestConfig } from 'axios';

import Logger from './logger';

/**
 * A type guard is a function that returns a boolean value and
 * has a special return type, like data is T. This return type indicates to
 * TypeScript that the data can be safely treated as type T within the guarded block.
 * @param data
 * @param typeGuard
 * @returns
 */
export function isOfType<T>(
  data: unknown,
  typeGuard: (data: unknown) => data is T
): data is T {
  return typeGuard(data);
}

/**
 * A generic function to call an API and return the response.
 *
 * @param url - The URL of the API endpoint.
 * @param options - The options for the fetch request, such as method, headers, body, etc.
 * @returns A promise that resolves to the response data of the specified type.
 * @throws An error if the API request fails or the response format is unexpected.
 */
export async function fetchApi<T>(
  url: string,
  options: AxiosRequestConfig,
  typeGuard: (data: unknown) => data is T
): Promise<T> {
  try {
    Logger.info('API call in progress - ', {
      url,
      ...options,
      headers: {
        'Content-Type': 'application/json',
        tenantId: 'dummy-tenant-id',
        ...(options.headers ?? {}),
      },
    });

    const response = await axios({
      url,
      ...options,
      headers: {
        'Content-Type': 'application/json',
        tenantId: 'dummy-tenant-id',
        ...(options.headers ?? {}),
      },
    });

    if (!isOfType<T>(response.data, typeGuard)) {
      throw new Error('Unexpected response format');
    }

    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      // Handle Axios-specific errors
      Logger.error('Error fetching API:', {
        message: err.message,
        response: err.response,
      });
    } else {
      // Handle any other errors
      Logger.error('Error fetching API:', { err });
    }

    throw err;
  }
}
